/**
 * ContactsSection — contactpersonen op de B2B-klantpagina (Golf 3-herbouw).
 *
 * Van statische chips-wolk naar werkende lijst: alfabetisch, zoek/filter-veld,
 * standaard ingeklapt tot 8 rijen met "toon alle N", en elke rij klikt door
 * naar de ContactDrawer (detail + orders van die persoon). De bestaande
 * add/edit/delete/consent-flows blijven als hover-acties op de rij.
 */
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { ContactInfo } from '../../stores/klanten-store';

const COLLAPSED_ROWS = 4;

interface Props {
  contacts: ContactInfo[];
  canAddContact?: boolean;
  onAddContact?: () => void;
  onEditContact?: (contact: ContactInfo) => void;
  onDeleteContact?: (contact: ContactInfo) => void;
  onToggleConsent?: (contact: ContactInfo) => void;
  onOpenContact?: (contact: ContactInfo) => void;
}

export function ContactsSection({
  contacts,
  canAddContact = false,
  onAddContact,
  onEditContact,
  onDeleteContact,
  onToggleConsent,
  onOpenContact,
}: Props) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () => [...contacts].sort((a, b) => a.name.localeCompare(b.name, 'nl')),
    [contacts]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) =>
      `${c.name} ${c.email} ${c.phone} ${c.title} ${c.department}`.toLowerCase().includes(q)
    );
  }, [sorted, search]);

  const visible = expanded ? filtered : filtered.slice(0, COLLAPSED_ROWS);
  const hiddenCount = filtered.length - visible.length;

  if (contacts.length === 0 && !canAddContact) return null;

  const canMutate = !!(onEditContact || onDeleteContact || onToggleConsent);

  return (
    <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex-shrink-0">
          Contactpersonen ({contacts.length})
        </h3>
        {contacts.length > COLLAPSED_ROWS && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek contactpersoon…"
            className="flex-1 max-w-[260px] px-2.5 py-1 border border-slate-200 rounded text-xs bg-white placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none"
          />
        )}
        {canAddContact && onAddContact && (
          <button
            type="button"
            onClick={onAddContact}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex-shrink-0"
          >
            + Contactpersoon
          </button>
        )}
      </div>

      {contacts.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Nog geen contactpersonen.</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Geen contactpersonen matchen &lsquo;{search.trim()}&rsquo;.</p>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
            {visible.map((c) => (
              <div
                key={c.id}
                className="group flex items-center gap-3 px-3 py-2 hover:bg-indigo-50/40 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onOpenContact?.(c)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  title="Open contactpersoon"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold flex items-center justify-center uppercase">
                    {c.name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('')}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-800 truncate">{c.name}</span>
                      {c.title && <span className="text-[11px] text-slate-400 truncate">{c.title}</span>}
                      {c.emailOptOut && (
                        <span
                          className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-[10px] font-medium"
                          title="Wil geen marketing-mails ontvangen"
                        >
                          Geen mail
                        </span>
                      )}
                    </span>
                    {(c.email || c.phone) && (
                      <span className="block text-[11px] text-slate-400 truncate">
                        {[c.email, c.phone].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                </button>
                {canMutate && (
                  <span className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {onToggleConsent && (
                      <button
                        type="button"
                        onClick={() => onToggleConsent(c)}
                        title={c.emailOptOut ? 'Marketing-mails weer aanzetten' : 'Marketing-mails uitzetten'}
                        aria-label={c.emailOptOut ? 'Marketing-mails weer aanzetten' : 'Marketing-mails uitzetten'}
                        className={clsx(
                          'p-1 rounded-full hover:bg-slate-100',
                          c.emailOptOut ? 'text-red-500 hover:text-red-600' : 'text-slate-400 hover:text-amber-600'
                        )}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3l18 18" />
                        </svg>
                      </button>
                    )}
                    {onEditContact && (
                      <button
                        type="button"
                        onClick={() => onEditContact(c)}
                        title="Bewerk contactpersoon"
                        aria-label="Bewerk contactpersoon"
                        className="p-1 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        </svg>
                      </button>
                    )}
                    {onDeleteContact && (
                      <button
                        type="button"
                        onClick={() => onDeleteContact(c)}
                        title="Verwijder contactpersoon"
                        aria-label="Verwijder contactpersoon"
                        className="p-1 text-slate-400 hover:text-red-600 rounded-full hover:bg-slate-100"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              Toon alle {filtered.length} contactpersonen
            </button>
          )}
          {expanded && filtered.length > COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="mt-2 text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              Toon minder
            </button>
          )}
        </>
      )}
    </div>
  );
}
